import React, { useEffect, useState } from 'react';

import { isEmpty } from 'lodash';
import moment from 'moment';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

import ActionButton from '../../Common/ActionButton';
import PanelBlock from '../../Common/PanelBlock';

import Refund, { allowedRefundAllocationTypes } from '../../../Entities/Refund';
import Field from '../../Fields/Field';
import InputSelectField from '../../Fields/InputSelectField';

import { queryGetFacilitySettings, queryGetPaymentApplicationMonths, queryReversePayment } from '../../../Queries';

import { invoiceQueryFields } from './InvoicePanel';

import Invoice from '../../../Entities/Invoice';
import Payment from '../../../Entities/Payment';
import PaymentApplicationMonth from '../../../Entities/PaymentApplicationMonth';

import LoadingState from '../../Common/LoadingState';

import featureFlags from '../../../featureFlags';
import useConfiguration from '../../../Hooks/useConfiguration';
import './InvoiceRefundPanel.scss';

const getFieldConfiguration = async () => {
  const { allocation } = await Refund.getRefundAllocationTypes();
  const allowedRefundAllocationTypeOptions = allocation.options.filter(option => allowedRefundAllocationTypes.includes(option.value));

  const { type } = await Payment.getFieldAllowedValues([ 'type' ]);
  const types = type ? type.options : [];

  return {
    type: { options: allowedRefundAllocationTypeOptions },
    payment_type: types.reduce((obj, type) => {
      return { ...obj, [type.value]: type.label };
    }, {}),
  };
}

const InvoiceRefundPanel = ({ invoice, panelHeading, onSave, onCancel, facilityId, transactions }) => {
  const [ allocationType, setAllocationType ] = useState(null);
  const [ paymentId, setPaymentId ] = useState(null);
  const [ actionType, setActionType ] = useState('REVERSE');
  const [disableActionType, setDisableActionType] = useState(false);
  const [ refundSaveInProgress, setRefundSaveInProgress ] = useState(false);
  const [ applicationDate, setApplicationDate ] = useState(new Date());
  const [ paymentApplicationDate, setPaymentApplicationDate ] = useState(null);
  const [ dateRequired, setDateRequired ] = useState(true);
  const [ state, setState ] = useState({
    paymentApplicationMonths: [],
    futureMonthApplication: ''
  });
  const [ showError, setShowError] = useState(false)
  const {
    complete: loadingComplete,
    errors: loadingErrors,
    fieldConfiguration,
  } = useConfiguration(getFieldConfiguration);

  useEffect(() => {
    const getPaymentApplication = async() => {
      const applicationMonthsQuery = [
        'month',
        'year',
        'status',
      ];

      const monthsResponse = await queryGetPaymentApplicationMonths({
        facility_id: facilityId
      }, applicationMonthsQuery);

      const responseMonths = monthsResponse.data.getPaymentApplicationMonths;
      const paymentApplicationMonths = responseMonths.map(month => PaymentApplicationMonth.createFromQueryValues(month));

      const facilitySettingsFields = ['future_month_application'];

      const settingsResponse = await queryGetFacilitySettings({
        facility_id: facilityId
      }, facilitySettingsFields);

      const facilitySettings = settingsResponse.data.getFacilitySettings;
      const futureMonthApplication = facilitySettings?.future_month_application;

      setState({ paymentApplicationMonths, futureMonthApplication })
    }
   
    if(featureFlags.REVERSE_APPLICATIONDATE) {
      getPaymentApplication()
    }
  }, []);



  useEffect(() => {
    const payment = invoice.reversablePayments.find(payment => payment.fields.id.value === paymentId);
    if (
      actionType === 'UNAPPLY_CREDIT'
      && (
        payment?.fields.credit_entry.value?.credit.application_type === 'APPLY_CASH'
        || payment?.fields.credit_entry.value?.credit.application_type === 'CARRYOVER_CREDIT'
        || payment?.fields.credit_entry.value?.credit.application_type === 'DISCOUNT_CREDIT'
      )) {
      setApplicationDate(null);
      setDateRequired(false);
    } else {
      setDateRequired(true);
      handleApplicationDate();
    }
  }, [paymentId, actionType]);

  const handleNoAppDatesAvailable = (allowed) => {
    if (allowed) {
      setApplicationDate(null)
      setDateRequired(false)
      setShowError(true)
    } else {
      setDateRequired(true)
      setShowError(false)
    }
  }

  const handleApplyRefundSaveClick = async () => {
    try {
      setRefundSaveInProgress(true);

      const applicationDateFormatted = applicationDate ? moment(applicationDate).format('YYYY-MM-DD') : null;
      const payload = {
        payment_id: paymentId,
        reversal_type: actionType,
        ...featureFlags.REVERSE_APPLICATIONDATE ? { application_date: applicationDateFormatted } : {},
      };

      const res = await queryReversePayment(payload, invoiceQueryFields);
      if (res.errors) {
        throw new Error(res.errors.map(error => error.message));
      }

      const invoiceData = res.data.reversePayment.invoice;
      const credit_reversed = res.data.reversePayment.credit_reversed;
      const overpayment_credit = res.data.reversePayment.overpayment_credit;

      onSave({
        invoice: Invoice.createFromQueryValues(invoiceData),
        credit_reversed,
        overpayment_credit
      });
    } catch (error) {
      alert('An error occurred while reversing invoice payment:\n\n' + error.message + '\n');
    } finally {
      setRefundSaveInProgress(false);
    }
  }

  const handleApplyRefundCancelClick = () => {
    onCancel({});
  }

  const getPastDatesToExclude = () => {
    const { paymentApplicationMonths } = state;

    if (!paymentApplicationMonths.length) return [];

    const closedMonths = paymentApplicationMonths.filter(month => month.fields.status.value === "CLOSED");

    // Exclude every day of every closed month
    const datesToExclude = closedMonths.map(closedMonth => {
      const year = closedMonth.fields.year.value;

      let month = closedMonth.fields.month.value.toLowerCase();
      month = month.charAt(0).toUpperCase() + month.slice(1);
      month = moment(`${month}, ${year}`, 'MMM YYYY');

      const daysInMonth = month.daysInMonth();
      return [...new Array(daysInMonth)].map((_, i) => {
        return new Date(month.date(i + 1));
      });
    }).flat();

    return datesToExclude;
  };

  const datesToExclude = getPastDatesToExclude();
  let startDate = new Date(moment().subtract(1, "year").startOf("month").add(1, "day").format("YYYY-MM-DD"));
  const tomorrow = new Date(moment().add(1, "day").format('YYYY-MM-DD'));
  const currentMonth = new Date(moment().startOf("month").add(1, "month").format('YYYY-MM-DD'));

  let maxDate = isEmpty(datesToExclude) ? tomorrow :
      state.futureMonthApplication === "NOT_ALLOWED" ? tomorrow :
        state.futureMonthApplication === "ALLOWED_CURRENT_MONTH" ? currentMonth : "";
  startDate = paymentApplicationDate ?? startDate

  const handleApplicationDate = () => {
    const payment = invoice.reversablePayments.find(payment => payment.fields.id.value === paymentId);
    const transaction = transactions.find(transaction => transaction.line_item_payment?.value?.fields.payment.value.fields.id.value === payment?.fields.id.value);
    
    const overpaymentCreditEntry = payment?.fields.credit.value?.credit_entries.find(creditEntry => creditEntry.type === 'OVERPAYMENT');
    const overpaymentAppDate = overpaymentCreditEntry?.application_date;
    const creditCreationEntry = (payment?.fields.credit_entry.value?.credit?.credit_entries ?? []).find(creditEntry => creditEntry.type === 'CREATION');
    const creditCreationAppDate = creditCreationEntry?.application_date;
    
    let newAppDate = new Date(transaction.application_date);

    if (overpaymentAppDate) {
      newAppDate = moment(overpaymentAppDate).isAfter(transaction.application_date) ? moment(overpaymentAppDate).toDate() : new Date(transaction.application_date);
    } else if (creditCreationAppDate && payment?.fields.credit_entry.value?.credit?.application_type === 'APPLY_CASH') {
      newAppDate = moment(creditCreationAppDate).toDate();
    }

    if (transaction.application_date || creditCreationAppDate) {
      if (moment(newAppDate).isAfter(maxDate)) {
        handleNoAppDatesAvailable(true);
      } else {
        handleNoAppDatesAvailable(false);
        setApplicationDate(newAppDate);
        setPaymentApplicationDate(newAppDate);
      }
    }
  };
  
  const isApplicationDateExcluded = datesToExclude.find(date => moment(date).isSame(moment(applicationDate), 'day'));
  const application_date = isApplicationDateExcluded && applicationDate !== null ? new Date() : applicationDate;

  const updatePaymentId = (paymentId) => {
    setPaymentId(paymentId);
    if (featureFlags.UNAPPLY_CREDIT) {
      const selectedPayment = invoice.reversablePayments.find(payment => payment.fields.id.value === paymentId);
      if (selectedPayment) {
        if (selectedPayment.fields.type.value === 'CREDIT') {
          setDisableActionType(false);
        } else {
          setActionType('REVERSE');
          setDisableActionType(true);
        }
      }
    }
  };
  const renderInputs = () => {
    const paymentInputOptions = invoice.reversablePayments.map(payment => {
      const creditAmount = payment.fields.credit.value?.amount || 0;
      const paymentAmount = payment.fields.amount.value;
      const paymentType = payment.fields.type.value;
      const amount = featureFlags.UNAPPLY_CREDIT ? paymentAmount + creditAmount : paymentAmount;
      const label = featureFlags.PAYMENT_TYPES ?
        `${payment.fields.type.value} - $${(amount / 100).toFixed(2)}` :
        `${paymentType} - $${(amount / 100).toFixed(2)}`
      return {
        value: payment.fields.id.value,
        label: label
      }
    });
    const actionOptions = [{ value: 'REVERSE', label: 'Reverse' }];
    if(featureFlags.UNAPPLY_CREDIT){
      actionOptions.push({value:'UNAPPLY_CREDIT', label: 'Unapply Credit'})
    }
    return (
      <div className="parkchirp--invoice-refund-panel--main-inputs-container">
        <Field label="Action"
          id="parkchirp--refund--action"
          containerClasses={[ 'parkchirp--refund-input-block' ]}
          layout="stacked"
        >
          <InputSelectField
            inputId="parkchirp--refund--action"
            options={actionOptions}
            value={actionType}
            editMode={true}
            disabledMode={disableActionType}
            onChange={setActionType}
          />
        </Field>
        <Field label="Payment"
          id="parkchirp--refund--payment-id"
          containerClasses={[ 'parkchirp--refund-input-block' ]}
          layout="stacked"
        >
          <InputSelectField
            inputId="parkchirp--refund--payment-id"
            options={[{ value: null, label: '' }, ...paymentInputOptions ]}
            value={paymentId}
            onChange={updatePaymentId}
            editMode={true}
          />
        </Field>
        <Field label="Allocation"
          id="parkchirp--refund--allocation-type"
          containerClasses={[ 'parkchrip--refund-input-block' ]}
          layout="stacked"
        >
          <InputSelectField inputId="parkchirp--refund--allocation-type"
            options={[{ value: null, label: '' }, ...fieldConfiguration.type.options ]}
            value={allocationType}
            onChange={setAllocationType}
            editMode={true}
          />
        </Field>
        {featureFlags.REVERSE_APPLICATIONDATE &&
            <Field label="Application Date"
              id="parkchirp--payment--application-date"
              containerClasses={['parkchirp--payment-input-block']}
              layout="stacked">

              <DatePicker
                id={`parkchirp--payment--application-date-field`}
                selected={application_date}
                onChange={date => setApplicationDate(date)}
                excludeDates={datesToExclude}
                minDate={startDate}
                maxDate={maxDate}
                placeholderText={dateRequired ? '' : '-'}
                disabled={!dateRequired}
              />
              {!applicationDate && dateRequired && <span className='error'>Provide a valid date</span>}
              {showError &&  <span className='error'>There are no application dates available. Please reopen an accounting period to make this reversal.</span>}
            </Field>
          }
      </div>
    )
  }
  return (
    <div className="parkchirp--invoice-refund-panel">
      <PanelBlock heading={panelHeading}
        actions={[
          <ActionButton id="parkchirp--invoice-refund-action-button" label="Save"
            inProgressState={refundSaveInProgress}
            onClick={handleApplyRefundSaveClick}
            disabled={showError}
          />,
          <button type="button"
            className="parkchirp--button-as-link"
            style={{paddingLeft: '10px', paddingRight: '10px'}}
            onClick={handleApplyRefundCancelClick}>
            Cancel
          </button>
        ]}
      >
        <LoadingState
          processingMessage="Initializing refund, please wait..."
          errorMessage="The following errors occurred while initializing the refund:"
          complete={loadingComplete}
          errors={loadingErrors}
          render={renderInputs}
        />
      </PanelBlock>
    </div>
  )
}

export default InvoiceRefundPanel;